import * as React from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import {
  LayoutDashboard,
  FileText,
  Calendar,
  Mail,
  MessageSquare,
  ImageIcon,
  CreditCard,
  Settings,
  Building2,
  Users,
  ScrollText,
  ShieldCheck,
  Rocket,
  TrendingUp,
} from 'lucide-react';

import { cn } from '@/lib/cn';
import type { Role } from '@/lib/auth';

interface NavItem {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  /** Roles allowed to see this entry. Operator sees everything. */
  roles?: Role[];
}

interface NavGroup {
  title: string;
  items: NavItem[];
}

const CLINIC_NAV: NavGroup[] = [
  {
    title: 'Przegląd',
    items: [{ to: '/', label: 'Pulpit', icon: LayoutDashboard }],
  },
  {
    title: 'Strona',
    items: [
      { to: '/brief', label: 'Treść strony', icon: FileText },
      { to: '/media', label: 'Media', icon: ImageIcon },
      { to: '/publish', label: 'Publikacja', icon: Rocket },
    ],
  },
  {
    title: 'Pacjenci',
    items: [
      { to: '/calendar', label: 'Kalendarz wizyt', icon: Calendar },
      { to: '/coordinate', label: 'Koordynacja lekarzy', icon: Calendar },
      { to: '/messages', label: 'Wiadomości', icon: Mail },
      { to: '/sms', label: 'SMS', icon: MessageSquare },
    ],
  },
  {
    title: 'Wzrost',
    items: [
      { to: '/growth', label: 'Content marketplace', icon: TrendingUp },
    ],
  },
  {
    title: 'Konto',
    items: [
      { to: '/team', label: 'Zespół (dostęp)', icon: Users },
      { to: '/billing', label: 'Subskrypcja', icon: CreditCard },
      { to: '/settings', label: 'Ustawienia', icon: Settings },
    ],
  },
];

const OPERATOR_NAV: NavGroup[] = [
  {
    title: 'Operacyjne',
    items: [
      { to: '/operator', label: 'Pulpit', icon: LayoutDashboard },
      { to: '/operator/tenants', label: 'Klienci', icon: Building2 },
      { to: '/operator/content-orders', label: 'Zamówienia content', icon: TrendingUp },
      { to: '/operator/conversations', label: 'Skrzynka', icon: Mail },
      { to: '/operator/users', label: 'Użytkownicy', icon: Users },
    ],
  },
  {
    title: 'Treść',
    items: [
      { to: '/operator/services', label: 'Biblioteka usług', icon: FileText },
      { to: '/operator/forbidden', label: 'Forbidden claims', icon: ShieldCheck },
    ],
  },
  {
    title: 'Audyt',
    items: [{ to: '/operator/audit', label: 'Audit log', icon: ScrollText }],
  },
];

export interface SidebarProps {
  role: Role;
  brandLabel: string;
  brandSublabel?: string;
}

export function Sidebar({ role, brandLabel, brandSublabel }: SidebarProps) {
  const groups = role === 'operator' ? OPERATOR_NAV : CLINIC_NAV;
  return (
    <nav
      aria-label="Główna nawigacja"
      className="flex h-full w-60 flex-col border-r border-border bg-surface"
    >
      <div className="px-4 py-5 border-b border-border">
        <p className="font-display text-base font-semibold text-text">{brandLabel}</p>
        {brandSublabel ? (
          <p className="mt-0.5 text-xs text-text-subtle truncate">{brandSublabel}</p>
        ) : null}
      </div>
      <div className="flex-1 overflow-y-auto scrollbar-thin py-3 px-2">
        {groups.map((group) => (
          <div key={group.title} className="mb-4">
            <p className="px-3 py-1.5 text-xs font-medium uppercase tracking-wide text-text-subtle">
              {group.title}
            </p>
            <ul role="list" className="mt-1 space-y-0.5">
              {group.items.map((item) => (
                <li key={item.to}>
                  <SidebarLink to={item.to} label={item.label} Icon={item.icon} />
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </nav>
  );
}

function SidebarLink({
  to,
  label,
  Icon,
}: {
  to: string;
  label: string;
  Icon: React.ComponentType<{ className?: string }>;
}) {
  const matches = useRouterState({ select: (s) => s.matches });
  const active = matches.some((m) => m.pathname === to);
  return (
    <Link
      to={to}
      className={cn(
        'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
        active
          ? 'bg-bg text-text shadow-[0_1px_2px_rgba(0,0,0,0.04)] ring-1 ring-black/5'
          : 'text-text-muted hover:bg-bg/60 hover:text-text'
      )}
    >
      <Icon className="size-4 shrink-0" aria-hidden="true" />
      <span className="truncate">{label}</span>
    </Link>
  );
}
